import React, { useState, useEffect, useRef, useCallback } from "react";
import { StarRatingContainer, StyledStar } from "./index.styled.ts";
import { colors } from "../../../styles/colors";
import StarIcon from "../StarIcon";

interface StarRatingProps {
  rating: number;
  size?: number;
  onRatingChange?: (animatedRating: number) => void;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  size = 16,
  onRatingChange,
}) => {
  const [animatedRating, setAnimatedRating] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const startAnimation = useCallback(() => {
    setAnimatedRating(0);
    const timer = setTimeout(() => {
      const startTime = Date.now();
      const duration = 1500;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentRating = rating * easeOutCubic;

        setAnimatedRating(currentRating);
        onRatingChange?.(currentRating);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, 100);

    return () => clearTimeout(timer);
  }, [rating, onRatingChange]);

  useEffect(() => {
    if (isVisible) {
      const cleanup = startAnimation();
      return cleanup;
    }
  }, [isVisible, startAnimation]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: "0px 0px -50px 0px", // Start animation slightly before fully in view
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const renderStars = () => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      const fillPercentage = Math.min(Math.max(animatedRating - (i - 1), 0), 1);

      stars.push(
        <StyledStar
          key={i}
          style={{ width: size, height: size }}
          $fillPercentage={fillPercentage}
        >
          <StarIcon size={size} color={colors.lightBlueGray} filled={false} />

          <div className="star-clip">
            <StarIcon size={size} color={colors.yellow} filled={true} />
          </div>
        </StyledStar>
      );
    }

    return stars;
  };

  return (
    <StarRatingContainer ref={containerRef}>
      <div className="stars-container" dir="ltr">
        {renderStars()}
      </div>
    </StarRatingContainer>
  );
};

export default StarRating;
